import cx from 'classnames';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

import { Card, Pagination, RamblaActionIcon, Wrapper } from 'components';
import { PermissionContext } from 'components/common/ProtectedRoute';
import RequestPendingSpinner from 'components/RequestPendingSpinner/RequestPendingSpinner';
import entities from 'constants/entities';
import renderCell from 'functions/renderCell';
import withDefaultProps from 'helpers/withDefaultProps';
import * as matchSorter from 'match-sorter';
import PropTypes, { arrayOf } from 'prop-types';
import * as qs from 'qs';
import React, { useState } from 'react';
import { Table as BootstrapTable, Image } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import {
  useAsyncDebounce,
  useFilters,
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from 'react-table';
import Popup from 'reactjs-popup';
import { toastr } from 'utils';
import RamblaLogo from '../../assets/img/rambla-logo-black.png';
import { ReactComponent as ArrowDown } from '../../assets/svg/arrowDown.svg';
import { ReactComponent as ArrowNeutral } from '../../assets/svg/arrowNeutral.svg';
import { ReactComponent as ArrowUp } from '../../assets/svg/arrowUp.svg';
import { ReactComponent as CloseIcon } from '../../assets/svg/close.svg';
import Search from '../Search/search';
import { FilterButton } from './components/FilterButton';
import Filter, { sortQuery } from './filters/filter';
import useUrlQueryParamFilter from './filters/useUrlQueryParamFilter';
import {
  EmptyStateDescription,
  EmptyStateTitle,
  TableHeaderActions,
  TableHeaderContent,
  TableHeader,
  TableRow,
  TableSubtitle,
  TableTitle,
} from './styles';

export const TableContext = React.createContext();

const Table = ({
  data,
  pageSize = 10,
  loading = false,
  columns,
  children,
  sortBy: initialSortBy = [],
  fetchData,
  dataCount,
  serverPagination = false,
  initialFilters = [],
  isDashboardTable = false,
  ...restProps
}) => {
  const memoizedColumns = React.useMemo(() => columns, [columns]);
  const memoizedData = React.useMemo(() => data, [data]);
  const isPaginable = (serverPagination ? dataCount : data?.length) > pageSize;
  const browserParams = useLocation();

  const previousPageRef = React.useRef(0);
  const initialRunRef = React.useRef(true);

  const filteredValues = qs.parse(browserParams.search, { ignoreQueryPrefix: true });
  const initialFiltersWithQuery = [...initialFilters];

  Object.keys(filteredValues).forEach((key) => {
    initialFiltersWithQuery.push({ id: key, value: filteredValues[key] });
  });

  function fuzzyTextFilterFn(rows, id, filterValue) {
    return matchSorter(rows, filterValue, { keys: [(row) => row.values[id]] });
  }
  const filterTypes = React.useMemo(
    () => ({
      // Add a new fuzzyTextFilterFn filter type.
      fuzzyText: fuzzyTextFilterFn,
      // Or, override the default text filter to use
      // "startWith"
      text: (rows, id, filterValue) =>
        rows.filter((row) => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue).toLowerCase().startsWith(String(filterValue).toLowerCase())
            : true;
        }),
      multiple: (rows, id, filterValue) =>
        rows.filter((row) => {
          // we need to parse id to int because on backed side we have id as a number
          // and ids we get from url query string
          const rowValue = id[0] === 'id' ? `${row.original[id]}` : row.original[id];

          return rowValue !== undefined ? Object.values(filterValue).includes(rowValue) : true;
        }),
    }),
    [],
  );

  const useTableParams = {
    data: memoizedData,
    columns: memoizedColumns,
    initialState: { pageIndex: 0, filters: initialFiltersWithQuery, sortBy: initialSortBy },
    filterTypes,
  };

  if (serverPagination) {
    useTableParams.manualPagination = true;
    useTableParams.manualFilters = true;
    useTableParams.manualGlobalFilter = true;
    useTableParams.manualSortBy = true;
    useTableParams.autoResetPage = false;
    useTableParams.pageCount = Math.ceil(dataCount / pageSize);
  }

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    visibleColumns,
    preFilteredRows,
    preGlobalFilteredRows,
    setGlobalFilter,
    setFilter,
    page,
    pageOptions,
    gotoPage,
    state: { pageIndex, filters, globalFilter, sortBy },
  } = useTable(useTableParams, useFilters, useGlobalFilter, useSortBy, usePagination);

  const [queryFilters, removeQueryFilter] = useUrlQueryParamFilter(memoizedColumns, setFilter);

  // Server side filtering
  const searchOptions = filters.reduce(
    (params, { id, value }) => ({
      ...params,
      [id]: value,
    }),
    {},
  );
  if (globalFilter) {
    searchOptions.search = globalFilter;
  }
  if (sortBy.length) {
    const { id, desc } = sortBy[0];

    const { sortKey, sortValue } = sortQuery(id, desc);
    searchOptions[sortKey] = sortValue;
  }
  const searchOptionsString = Object.keys(searchOptions) // for memoization
    .map((key) => [key, searchOptions[key]].join('='))
    .join('&');

  searchOptions.page = pageIndex + 1; // memoization without page to differantiate between changing filters and page

  React.useEffect(() => {
    if (!serverPagination || initialRunRef.current) {
      initialRunRef.current = false;
      return;
    }
    if (pageIndex !== previousPageRef.current) {
      // user changes page
      previousPageRef.current = pageIndex;
    } else if (pageIndex !== 0) {
      // user changes filter while on not initial page
      previousPageRef.current = 0;
      searchOptions.page = 1;
      gotoPage(0); // this would change pageIndex so we need to reset initialRun flag
      initialRunRef.current = true;
    }

    fetchData(searchOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchOptionsString, pageIndex]);
  const queryFiltersCount = Object.keys(filteredValues).length;
  React.useEffect(() => {
    if (serverPagination && queryFiltersCount > 0) {
      fetchData(searchOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryFiltersCount]);

  const [value, setValue] = React.useState('');
  const tableProps = getTableProps();
  tableProps.style = {
    ...(tableProps.style || {}),
    tableLayout: 'fixed',
    width: '100%',
  };

  return (
    <TableContext.Provider
      value={{
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows: page,
        prepareRow,
        state,
        visibleColumns,
        preFilteredRows,
        preGlobalFilteredRows,
        dataCount: serverPagination ? dataCount || 0 : (preGlobalFilteredRows || []).length,
        setGlobalFilter,
        value,
        setValue,
        setFilter,
        pageOptions,
        gotoPage,
        pageIndex,
        queryFilters,
        removeQueryFilter,
        loading,
        isPaginable,
        isDashboardTable,
      }}
    >
      <Card
        noPadding
        {...restProps}
        className={cx(restProps.className, {
          'border-0 shadow-none': isDashboardTable,
        })}
        style={{
          ...(restProps.style || {}),
          ...(isDashboardTable
            ? {
                border: 'none',
                boxShadow: 'none',
              }
            : {}),
        }}
      >
        {children}
      </Card>
    </TableContext.Provider>
  );
};

Table.Header = function Header({ title, subtitle, children, ...restProps }) {
  return (
    <TableHeader {...restProps} style={{ overflowX: 'auto' }}>
      <TableHeaderContent>
        <Table.Title>{title}</Table.Title>
        {subtitle && <TableSubtitle>{subtitle}</TableSubtitle>}
      </TableHeaderContent>
      {children && <TableHeaderActions>{children}</TableHeaderActions>}
    </TableHeader>
  );
};

Table.Title = function Title({ children, ...restProps }) {
  return <TableTitle {...restProps}>{children}</TableTitle>;
};

Table.Content = function Content({
  entity,
  warningMessage = {},
  noDataMessage = {},
  deletable = true,
  editable = true,
  sharable = false,
  onShare,
  onDelete,
  onRowClick,
  deletingItems = [],
  canDelete = () => true,
}) {
  const {
    getTableProps,
    headerGroups,
    getTableBodyProps,
    prepareRow,
    rows,
    pageOptions,
    gotoPage,
    pageIndex,
    loading,
    isPaginable,
    isDashboardTable,
  } = React.useContext(TableContext);
  const { permission } = React.useContext(PermissionContext) || {};
  const navigate = useNavigate();
  const NoData = () => (
    <Image src={RamblaLogo} style={{ width: '35%', height: 'auto', opacity: '0.9' }} />
  );
  const { t } = useTranslation(['toastr']);
  const firstPageRows = rows.slice(0, 10);
  const { style: tableStyleProps, ...restTableProps } = getTableProps();
  const hasColumnSizing = headerGroups.some((group) =>
    group.headers.some((column) => typeof column.width === 'string'),
  );

  const tableStyle = hasColumnSizing
    ? {
        ...(tableStyleProps || {}),
        tableLayout: 'fixed',
        width: '100%',
      }
    : tableStyleProps;
  const toCssSize = (value) =>
    value === undefined ? undefined : typeof value === 'number' ? `${value}px` : value;
  const getColumnSizeStyles = (column) => {
    const { size, width, minWidth, maxWidth } = column;
    const resolvedSize = size ?? width;

    return {
      ...(resolvedSize !== undefined ? { width: toCssSize(resolvedSize) } : {}),
      ...(minWidth !== undefined ? { minWidth: toCssSize(minWidth) } : {}),
      ...(maxWidth !== undefined ? { maxWidth: toCssSize(maxWidth) } : {}),
    };
  };
  const cellPaddingStyles = isDashboardTable ? { paddingTop: '10px', paddingBottom: '10px' } : {};

  return (
    <>
      <BootstrapTable responsive hover size="sm" {...restTableProps} style={tableStyle}>
        <>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers
                  .filter((column) => !column?.hidden)
                  .map((column) => {
                    const isCentered =
                      column.centered ||
                      ['id', 'dcount', 'views', 'sum', 'productsCount'].includes(column.id);
                    const isSortable = column.sortable === undefined ? true : column.sortable;
                    const sortedArrow = column.isSortedDesc ? <ArrowDown /> : <ArrowUp />;
                    const headerProps = column.getHeaderProps(
                      isSortable ? column.getSortByToggleProps() : undefined,
                    );
                    headerProps.style = {
                      ...(headerProps.style || {}),
                      ...getColumnSizeStyles(column),
                      ...cellPaddingStyles,
                    };
                    return (
                      <th
                        {...headerProps}
                        className={cx({
                          'text-center': isCentered,
                          'position-relative': true,
                        })}
                      >
                        {column.render('Header')}
                        <span style={{ fontSize: 9, position: 'absolute', marginLeft: 2 }}>
                          {isSortable && (column.isSorted ? sortedArrow : <ArrowNeutral />)}
                        </span>
                      </th>
                    );
                  })}
                {sharable && <th style={{ textAlign: 'center', width: '80px' }}>{t('share')}</th>}
                {deletable && permission?.delete && (
                  <th style={{ textAlign: 'center', width: '80px' }}>{t('delete')}</th>
                )}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {loading &&
              Array.from({ length: 5 }, (_, i) => (
                <TableRow key={`skeleton-${i}`} style={{ position: 'relative', height: 80 }}>
                  {headerGroups[0]?.headers
                    .filter((col) => !col.hidden)
                    .map((col) => (
                      <td
                        key={col.id}
                        style={{
                          ...getColumnSizeStyles(col),
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          ...cellPaddingStyles,
                        }}
                      >
                        <Skeleton width="75%" height={16} />
                      </td>
                    ))}
                  {sharable && (
                    <td style={{ textAlign: 'center', width: '80px', ...cellPaddingStyles }}>
                      <Skeleton height={16} width={24} />
                    </td>
                  )}
                  {deletable && permission?.delete && (
                    <td style={{ textAlign: 'center', width: '80px', ...cellPaddingStyles }}>
                      <Skeleton height={16} width={24} />
                    </td>
                  )}
                </TableRow>
              ))}
            {!loading &&
              firstPageRows.map((row) => {
                prepareRow(row);
                const isDeleting = deletingItems.includes(row.original.uid);
                return (
                  <TableRow
                    style={{ position: 'relative' }}
                    onClick={() => onRowClick && onRowClick(row.cells[0].row.original)}
                    clicable={!!onRowClick}
                    {...row.getRowProps()}
                  >
                    {row.cells
                      .filter((cell) => !cell.column?.hidden)
                      .map((cell, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <td
                          key={cell.column.id}
                          {...(() => {
                            const cellProps = cell.getCellProps();
                            cellProps.style = {
                              ...(cellProps.style || {}),
                              ...getColumnSizeStyles(cell.column),
                              verticalAlign: 'middle',
                              textAlign: 'center',
                              ...cellPaddingStyles,
                            };

                            return cellProps;
                          })()}
                        >
                          {index === 0 && isDeleting && (
                            <RequestPendingSpinner color="#be1622" isActive />
                          )}
                          {renderCell(navigate, cell, entity, editable && permission?.update)}
                        </td>
                      ))}
                    {sharable && (
                      <td style={{ textAlign: 'center', ...cellPaddingStyles }}>
                        <button
                          type="button"
                          className="btn btn-link px-1"
                          onClick={(event) => {
                            event.stopPropagation();
                            onShare(row.original.short_url);
                          }}
                        >
                          <RamblaActionIcon name="share" />
                        </button>
                      </td>
                    )}
                    {deletable && permission?.delete && (
                      <td style={{ textAlign: 'center', ...cellPaddingStyles }}>
                        <button
                          type="button"
                          className="btn btn-link px-1"
                          disabled={!canDelete(row.original)}
                          style={{ opacity: !canDelete(row.original) ? 0.3 : 1 }}
                          onClick={(event) => {
                            event.stopPropagation();
                            toastr.confirm(warningMessage?.heading || t('toastr:areYouSure'), {
                              onOk: () => {
                                onDelete?.(row.original);
                              },
                              description: warningMessage?.description,
                            });
                          }}
                        >
                          <i className="fe fe-trash" style={{ fontSize: 20 }} />
                        </button>
                      </td>
                    )}
                  </TableRow>
                );
              })}
          </tbody>
        </>
      </BootstrapTable>
      {!rows.length && !loading && (
        <div className="p-5 pt-0 d-flex flex-column align-items-center">
          <NoData />
          <EmptyStateTitle>{noDataMessage.heading || t('noData')}</EmptyStateTitle>
          <EmptyStateDescription>
            {noDataMessage.description || t('noDataSubtitle')}
          </EmptyStateDescription>
          {noDataMessage.actions && (
            <div className="d-flex flex-wrap justify-content-center mt-4" style={{ gap: 12 }}>
              {noDataMessage.actions}
            </div>
          )}
        </div>
      )}
      {!!rows.length && isPaginable && !loading && (
        <Pagination activePage={pageIndex} numberOfPages={pageOptions.length} goToPage={gotoPage} />
      )}
    </>
  );
};

Table.Filter = function FilterComponent({
  filters = [],
  withGlobalFilter = false,
  globalFilterProps,
  withHideIcon = true,
}) {
  const {
    preFilteredRows,
    setFilter,
    state: { filters: tableFilters },
    queryFilters,
    visibleColumns,
    removeQueryFilter,
  } = React.useContext(TableContext);
  const [curentlySelectedFilter, setCurentlySelectedFilter] = useState(withHideIcon ? [] : filters);

  const filterProps = { filters: tableFilters, setFilter, preFilteredRows };

  const onFilterClick = (filter) => {
    const filterAlreadyAdded = curentlySelectedFilter.find((f) => f.column === filter.column);
    if (filterAlreadyAdded) {
      setFilter(filter.column, filter.defaultValue || '');
      setCurentlySelectedFilter(curentlySelectedFilter.filter((f) => f.column !== filter.column));
    } else {
      setCurentlySelectedFilter([...curentlySelectedFilter, filter]);
    }
  };

  return (
    <div style={{ padding: '1rem 1.5rem' }}>
      <Wrapper d-flex>
        {withGlobalFilter && <Table.GlobalFilter {...globalFilterProps} />}
        {!!filters.length && withHideIcon && (
          <Popup
            closeOnDocumentClick
            trigger={FilterButton({
              number: curentlySelectedFilter.length + Object.keys(queryFilters).length,
            })}
            position="bottom right"
          >
            <>
              {/* FILTERS WHICH ARE APPLIED VIA QUERY PARAM BUT DOESNT HAVE INPUT TO CHANGE IT */}
              {Object.keys(queryFilters).map((filterName) => {
                if (!filters.find((filter) => filter.column === filterName)) {
                  return (
                    <div className="d-flex" key={filterName}>
                      <label className="form-label">
                        {visibleColumns.find((column) => column.id === filterName).Header}
                      </label>
                      <CloseIcon
                        style={{ color: 'var(--bs-red)', cursor: 'pointer' }}
                        onClick={() => removeQueryFilter(filterName)}
                      />
                    </div>
                  );
                }
                return null;
              })}

              {filters.map((filter) => (
                // <Filter key={filter.column} {...filter} {...filterProps} />
                <button
                  key={filter?.key || filter.column}
                  type="button"
                  style={{ display: 'block' }}
                  className={`btn btn-${
                    curentlySelectedFilter.find((f) => f.column === filter.column)
                      ? 'primary'
                      : 'white'
                  } px-4 my-3 w-100 text-capitalize`}
                  onClick={() => onFilterClick(filter)}
                >
                  {filter.name || filter.column}
                </button>
              ))}
            </>
          </Popup>
        )}
      </Wrapper>
      {!!curentlySelectedFilter.length && (
        <div className="d-flex flex-row align-items-end mt-3">
          <div className="d-flex flex-row flex-wrap" style={{ gap: 20 }}>
            {curentlySelectedFilter.map((filter) => (
              <Filter key={filter.column} {...filter} {...filterProps} withCaption />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

Table.GlobalFilter = function GlobalFilterComponent({ entity, style }) {
  const { dataCount, value, setValue, setGlobalFilter } = React.useContext(TableContext);
  const onChange = useAsyncDebounce((val) => {
    setGlobalFilter(val || undefined);
  }, 200);

  const placeholder =
    dataCount === 1
      ? `Search 1 ${entity || 'record'}...`
      : `Search ${dataCount} ${entity || 'record'}s...`;

  return (
    <Search
      style={style}
      value={value}
      onChange={(e) => {
        setValue(e);
        onChange(e);
      }}
      placeholder={placeholder}
    />
  );
};

// /////////* Proptypes *//////////

Table.propTypes = {
  data: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      Header: PropTypes.string.isRequired,
      accessor: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
      Filter: PropTypes.func,
      filter: PropTypes.string,
      width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  ),
  sortBy: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      desc: PropTypes.bool.isRequired,
    }),
  ),
  isDashboardTable: PropTypes.bool,
  ...withDefaultProps(),
};

Table.Header.propTypes = { ...withDefaultProps() };

Table.Title.propTypes = { ...withDefaultProps() };

Table.Content.propTypes = {
  entity: PropTypes.oneOf(Object.keys(entities)),
  warningMessage: PropTypes.shape({
    heading: PropTypes.string,
    description: PropTypes.string,
  }),
  noDataMessage: PropTypes.shape({
    heading: PropTypes.node,
    description: PropTypes.node,
    actions: PropTypes.node,
  }),
  deletable: PropTypes.bool,
  editable: PropTypes.bool,
  onDelete: PropTypes.func,
  onRowClick: PropTypes.func,
  canDelete: PropTypes.func,
};

Table.Filter.propTypes = {
  filters: arrayOf(
    PropTypes.shape({
      column: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['slider', 'text', 'numberRange', 'select']),
    }),
  ),
  withGlobalFilter: PropTypes.bool,
  globalFilterProps: PropTypes.shape({
    entity: PropTypes.string,
    style: PropTypes.object,
  }),
  withHideIcon: PropTypes.bool,
};

Table.GlobalFilter.propTypes = {
  entity: PropTypes.string,
  style: PropTypes.object,
};

export default Table;
